export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
  speaker?: string;
}

export interface TextMatch {
  start: number;
  end: number;
}

// Transcript timestamps can come from parsed floating-point values with microsecond precision.
// This keeps exact-start matching stable without widening comparisons enough to blur segment boundaries.
const TRANSCRIPT_TIME_EPSILON = 1e-6;

const isSameTranscriptTime = (left: number, right: number): boolean =>
  Math.abs(left - right) <= TRANSCRIPT_TIME_EPSILON;

interface TranscriptTimeMatchState {
  containingSegmentIndex: number;
  nextSegmentIndex: number;
  previousSegmentIndex: number;
}

const updateTranscriptTimeMatchState = (
  state: TranscriptTimeMatchState,
  segment: TranscriptSegment,
  index: number,
  time: number,
): number | null => {
  if (isSameTranscriptTime(segment.start, time)) {
    return index;
  }

  if (state.containingSegmentIndex < 0 && time > segment.start && time < segment.end) {
    state.containingSegmentIndex = index;
  }

  if (segment.start < time) {
    state.previousSegmentIndex = index;
    return null;
  }

  if (state.nextSegmentIndex < 0 && segment.start > time) {
    state.nextSegmentIndex = index;
  }

  return null;
};

/**
 * Resolves a transcript timestamp with deterministic precedence:
 * exact segment start, then nearest earlier segment start, then nearest later.
 *
 * `transcriptSegments` is expected to be sorted by `start` ascending.
 * That is already true in the normal transcript pipeline: parsing preserves
 * source order, and incremental merges preserve append order from the asset.
 *
 * For timestamps inside a segment, the containing segment is treated as the
 * nearest earlier segment because playback should stay anchored to the segment
 * already covering that time.
 */
export function findTranscriptSegmentIndexForTime(
  transcriptSegments: TranscriptSegment[],
  time: number,
): number {
  if (!Number.isFinite(time) || transcriptSegments.length === 0) {
    return -1;
  }

  const matchState: TranscriptTimeMatchState = {
    containingSegmentIndex: -1,
    nextSegmentIndex: -1,
    previousSegmentIndex: -1,
  };

  for (let index = 0; index < transcriptSegments.length; index += 1) {
    const exactMatchIndex = updateTranscriptTimeMatchState(
      matchState,
      transcriptSegments[index],
      index,
      time,
    );
    if (exactMatchIndex !== null) {
      return exactMatchIndex;
    }
  }

  if (matchState.containingSegmentIndex >= 0) {
    return matchState.containingSegmentIndex;
  }

  if (matchState.previousSegmentIndex >= 0) {
    return matchState.previousSegmentIndex;
  }

  return matchState.nextSegmentIndex;
}

export function resolveTranscriptSeekTime(
  transcriptSegments: TranscriptSegment[],
  time: number,
): number {
  const resolvedIndex = findTranscriptSegmentIndexForTime(transcriptSegments, time);
  return resolvedIndex >= 0 ? transcriptSegments[resolvedIndex].start : time;
}

export function findTextMatches(text: string, query: string): TextMatch[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return [];
  }

  const normalizedText = text.toLowerCase();
  const matches: TextMatch[] = [];
  let searchFromIndex = 0;

  while (searchFromIndex < text.length) {
    const start = normalizedText.indexOf(normalizedQuery, searchFromIndex);
    if (start === -1) {
      break;
    }

    const end = start + normalizedQuery.length;
    matches.push({ start, end });
    searchFromIndex = end;
  }

  return matches;
}

export function normalizeSearchMatchIndex(
  index: number | null | undefined,
  matchCount: number,
): number | null {
  if (matchCount === 0) {
    return null;
  }

  if (index == null) {
    return 0;
  }

  if (index < 0) {
    return null;
  }

  return ((index % matchCount) + matchCount) % matchCount;
}

// Type guard to validate transcript item structure
export function isValidTranscriptItem(item: unknown): item is Record<string, unknown> {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
}

// Helper to parse timestamp string like "0:24:16.458490" to seconds
export function parseTimestampToSeconds(timestamp: unknown): number | null {
  if (typeof timestamp !== 'string') {
    return null;
  }

  // Parse format "H:MM:SS.ffffff" or "MM:SS.ffffff" or "SS.ffffff"
  const parts = timestamp.split(':');
  let totalSeconds = 0;

  if (parts.length === 3) {
    // H:MM:SS.ffffff
    totalSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseFloat(parts[2]);
  } else if (parts.length === 2) {
    // MM:SS.ffffff
    totalSeconds = parseInt(parts[0]) * 60 + parseFloat(parts[1]);
  } else if (parts.length === 1) {
    // SS.ffffff
    totalSeconds = parseFloat(parts[0]);
  } else {
    return null;
  }

  return isNaN(totalSeconds) ? null : totalSeconds;
}

// Helper to safely extract numeric value
export function getNumericValue(value: unknown, defaultValue: number): number {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    // First try to parse as timestamp format
    const timestampSeconds = parseTimestampToSeconds(value);
    if (timestampSeconds !== null) {
      return timestampSeconds;
    }
    // Fall back to simple number parsing
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && isFinite(parsed)) {
      return parsed;
    }
  }
  return defaultValue;
}

// Helper to safely extract string value
export function getStringValue(value: unknown, defaultValue: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value != null) {
    return String(value);
  }
  return defaultValue;
}

// Helper to convert a transcript item to segment with validation
export function itemToSegment(item: unknown): TranscriptSegment | null {
  if (!isValidTranscriptItem(item)) {
    console.warn('Invalid transcript item structure:', item);
    return null;
  }

  // Use the specific field names from the actual data structure
  const start = getNumericValue(item.time_start, 0);
  const end = getNumericValue(item.time_end, 0);
  const text = getStringValue(item.transcript, '');
  // Map role to speaker display name
  const roleStr = getStringValue(item.role, 'System').toLowerCase();
  let speaker: string;
  switch (roleStr) {
    case 'agent':
      speaker = 'Assistant';
      break;
    case 'user':
      speaker = 'Human';
      break;
    case 'reporter':
      speaker = 'Reporter';
      break;
    default:
      speaker = 'System';
  }

  // Validate time values
  if (start < 0 || end < 0 || end < start) {
    console.warn('Invalid time values in transcript item:', { start, end });
    return null;
  }

  // Filter out transcripts with empty or whitespace-only text
  if (!text || text.trim().length === 0) {
    return null;
  }

  return {
    start,
    end,
    text,
    speaker,
  };
}

/**
 * Creates a unique key for segment deduplication.
 * Uses the full normalized segment identity so overlapping same-prefix turns from
 * different speakers are not collapsed.
 * This ensures consistent deduplication across unique and merge operations.
 */
export function createSegmentKey(segment: TranscriptSegment): string {
  // Use fixed precision (6 decimal places = microsecond precision)
  const startKey = segment.start.toFixed(6);
  const endKey = segment.end.toFixed(6);
  const speakerKey = segment.speaker ?? 'System';
  const textKey = segment.text.trim();
  return `${startKey}_${endKey}_${speakerKey}_${textKey}`;
}

/**
 * Removes duplicate segments while preserving order.
 * Uses the same deduplication logic as mergeTranscriptSegments.
 */
export function getUniqueSegments(segments: TranscriptSegment[]): TranscriptSegment[] {
  const segmentMap = new Map<string, TranscriptSegment>();

  for (const segment of segments) {
    const uniqueKey = createSegmentKey(segment);
    if (!segmentMap.has(uniqueKey)) {
      segmentMap.set(uniqueKey, segment);
    }
  }

  return Array.from(segmentMap.values());
}

/**
 * Merges and deduplicates transcript segments.
 * Uses the exact same deduplication logic as getUniqueSegments.
 */
export function mergeTranscriptSegments(
  existingSegments: TranscriptSegment[],
  newSegments: TranscriptSegment[],
): TranscriptSegment[] {
  // Use a Set for efficient duplicate detection
  const segments = getUniqueSegments([...existingSegments, ...newSegments]);
  // Return segments in their original order (no sorting!)
  return segments;
}

// Parse JSON array format
export function parseJsonArray(text: string): TranscriptSegment[] | null {
  try {
    const data = JSON.parse(text);

    if (Array.isArray(data)) {
      const parsed = data
        .map(itemToSegment)
        .filter((segment): segment is TranscriptSegment => segment !== null);

      if (parsed.length === 0 && data.length > 0) {
        console.warn('All transcript items failed validation');
        return null;
      }

      return parsed;
    }

    return null;
  } catch (error) {
    console.warn('Failed to parse JSON array:', error);
    return null;
  }
}

// Parse NDJSON format
export function parseNdjson(text: string): TranscriptSegment[] {
  const segments: TranscriptSegment[] = [];
  const lines = text.split('\n');
  let skippedCount = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    try {
      const data = JSON.parse(line);
      const segment = itemToSegment(data);

      if (segment) {
        segments.push(segment);
      } else {
        skippedCount++;
      }
    } catch (lineError) {
      console.error(`Error parsing transcript line ${i}:`, lineError);
      skippedCount++;
    }
  }

  if (skippedCount > 0) {
    console.warn(`Skipped ${skippedCount} invalid transcript lines`);
  }

  return segments;
}

/**
 * Main parsing function that handles both JSON array and NDJSON formats.
 * Returns unique segments using consistent deduplication logic.
 */
export function parseTranscript(text: string): TranscriptSegment[] {
  if (!text || text.trim().length === 0) {
    return [];
  }

  const trimmedText = text.trim();
  let segments: TranscriptSegment[] = [];

  // Try JSON array first
  const jsonResult = parseJsonArray(trimmedText);
  if (jsonResult) {
    segments = jsonResult;
  } else {
    // Fallback to NDJSON
    segments = parseNdjson(trimmedText);
  }

  // Remove duplicates using the same logic as merge operations
  return getUniqueSegments(segments);
}
